import React, { useRef, useEffect, useCallback } from 'react';

/**
 * Lightweight canvas-based signature pad.
 * Works with mouse (desktop) and touch/pointer (mobile/tablet).
 */
export default function SignaturePad({ onChange, disabled, value }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawing = useRef(false);
  const hasStrokes = useRef(false);

  // Initialize canvas once after mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const w = canvas.offsetWidth || 400;
    const h = canvas.offsetHeight || 150;
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctxRef.current = ctx;

    // Restore saved signature (read-only/re-render mode)
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, h);
      img.src = value;
      hasStrokes.current = true;
    }
  }, []); // eslint-disable-line

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    isDrawing.current = true;
    const p = getPoint(e);
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(p.x, p.y);
  }, [disabled]);

  const draw = useCallback((e) => {
    if (!isDrawing.current || disabled) return;
    e.preventDefault();
    const p = getPoint(e);
    ctxRef.current.lineTo(p.x, p.y);
    ctxRef.current.stroke();
  }, [disabled]);

  const endDraw = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    hasStrokes.current = true;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    hasStrokes.current = false;
    onChange?.(null);
  };

  return (
    <div>
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{ border: '2px solid #E7E5E4', background: 'white', height: '150px' }}
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-full"
          style={{ touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!value && !disabled && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none text-[#A8A29E] text-sm font-body select-none">
            Sign here using mouse or finger
          </span>
        )}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={clear}
          className="mt-1.5 text-xs text-[#A8A29E] hover:text-red-500 font-body transition-colors"
        >
          Clear Signature
        </button>
      )}
    </div>
  );
}
